import React from "react";

const TermsOfServices = () => {
  return (
    <div>
      <h1>Terms of Service</h1>
      <p>Last Updated: 11th August 2026</p>

      <p>
        These Terms of Service govern access to and use of the Pogforts services.
        The services are owned and operated by Paul Frederick David Walsh
        ("Owner", "Operator", "we", "us" or "our"). They include the website
        and web application available at pogforts.uk, the Pogforts Discord server,
        the Magical Sorting Bat Discord bot ("MSB"), Pogforts: Murder Mystery
        Magic and associated game features, community features, authenticated
        account features, virtual game systems, content, tools and related services
        that we make available from time to time (collectively, the "Services").
      </p>

      <p>
        By accessing or using any part of the Services, you agree to these Terms.
        If you do not agree, you must not use the Services. These Terms should be
        read together with our Privacy Policy and Equality Policy, as updated from
        time to time.
      </p>

      <h2>1. Owner and Legal Status</h2>
      <ul>
        <li>
          The Services are owned and operated by Paul Frederick David Walsh in an
          individual capacity unless a specific Service expressly identifies a
          different contracting party.
        </li>
        <li>
          Any inconsistent ownership or operator statement in older Pogforts
          materials is superseded by the ownership and operator position stated in
          these current Terms for the Services covered by them.
        </li>
        <li>
          Nothing in these Terms creates a partnership, employment relationship,
          agency, fiduciary relationship or joint venture between you and the
          Owner.
        </li>
      </ul>

      <h2>2. Eligibility and Account Requirements</h2>
      <ul>
        <li>
          You must be legally permitted to use the Services in the place where you
          live and must meet any minimum age requirement imposed by applicable law,
          Discord or another platform through which a Service is accessed.
        </li>
        <li>
          Where access requires Discord authentication, you are responsible for
          maintaining control of your Discord account and for activity carried out
          through that account.
        </li>
        <li>
          You must provide accurate information where a Service requests it and
          must not impersonate another person, misrepresent your authority or use
          another person's account without permission.
        </li>
        <li>
          We may refuse, restrict or remove access where reasonably necessary for
          security, legal compliance, community safety, enforcement of these Terms
          or protection of the Services.
        </li>
      </ul>

      <h2>3. Discord and Third-Party Platform Rules</h2>
      <p>
        Some Services depend on or interact with third-party platforms, including
        Discord. Your use of those platforms remains subject to their own terms,
        policies and technical restrictions. A breach of an applicable third-party
        platform rule may also result in restricted access to the relevant part of
        the Services where continued access would create legal, security or
        operational risk.
      </p>

      <h2>4. Acceptable Use</h2>
      <p>You must not use the Services to:</p>
      <ul>
        <li>commit, encourage or facilitate unlawful activity;</li>
        <li>
          harass, threaten, abuse, exploit, discriminate against or deliberately
          endanger another person;
        </li>
        <li>
          distribute malware, malicious code, credential-stealing material,
          destructive payloads or content designed to compromise systems or users;
        </li>
        <li>
          attempt to gain unauthorised access to accounts, administrative areas,
          infrastructure, databases, APIs or other systems;
        </li>
        <li>
          probe, exploit, bypass or interfere with security controls, access
          restrictions, rate limits, authentication, game integrity or anti-abuse
          controls except where we have expressly authorised security testing;
        </li>
        <li>
          automate, scrape, harvest, mirror or extract data from the Services in a
          manner that breaches law, these Terms or the rights of another person;
        </li>
        <li>
          manipulate game systems, virtual balances, rankings, statistics,
          outcomes or other features through cheating, collusion, exploits or
          unauthorised tools;
        </li>
        <li>
          upload, publish or transmit content that infringes intellectual property,
          privacy, confidentiality or other legal rights; or
        </li>
        <li>
          use the Services in a way that materially disrupts availability,
          performance, community operations or another user's lawful use.
        </li>
      </ul>

      <h2>5. Community Conduct and User Content</h2>
      <ul>
        <li>
          You remain responsible for content, messages, media, links and other
          material that you submit or communicate through community areas.
        </li>
        <li>
          We may moderate, restrict, remove or preserve content where reasonably
          necessary to enforce rules, investigate reports, maintain community
          safety, protect legal rights or comply with law.
        </li>
        <li>
          We do not endorse user-generated content merely because it appears in a
          Pogforts community space. Views expressed by users are their own.
        </li>
        <li>
          If you believe content infringes your rights or breaches these Terms,
          report it through the designated Pogforts Discord channels or other
          contact method we make available.
        </li>
      </ul>

      <h2>6. Game Features, Virtual Currency and Random Outcomes</h2>
      <ul>
        <li>
          Pogforts may include fictional game mechanics, virtual balances,
          statistics, combat systems, rewards, penalties, chance-based mechanics
          and simulated gambling-style features such as roulette.
        </li>
        <li>
          Unless expressly stated otherwise for a specific transaction, virtual
          items, statistics, balances and "Pogs" exist only within the Services,
          are not legal tender, are not a bank deposit, do not constitute a
          financial product and have no guaranteed cash value or redemption value.
        </li>
        <li>
          Game outcomes may be determined by programmed rules or randomisation.
          We do not guarantee any particular outcome, ranking, reward, item,
          balance or progression result.
        </li>
        <li>
          We may correct balances, records or outcomes where we reasonably identify
          an error, exploit, duplicate transaction, corrupted state or abuse,
          provided that we do not remove statutory consumer rights that cannot
          lawfully be excluded.
        </li>
      </ul>

      <h2>7. Paid Services and Transactions</h2>
      <ul>
        <li>
          Some areas of the Services may allow purchases, bookings or payments for
          separate goods or services. The price, description and material terms
          displayed at the point of purchase form part of the relevant transaction.
        </li>
        <li>
          Payment processing may be provided by an independent payment service
          provider. That provider may apply its own terms and privacy notice to the
          payment process.
        </li>
        <li>
          Nothing in these Terms excludes or restricts any statutory consumer right
          that cannot legally be excluded, including rights that may apply under
          the Consumer Rights Act 2015 and other applicable consumer protection law.
        </li>
        <li>
          If a specific paid service is supplied under additional booking,
          cancellation, refund or service terms, those additional terms apply to
          that transaction alongside these Terms. If there is a conflict, the more
          specific transaction term applies to that subject matter, subject always
          to mandatory law.
        </li>
      </ul>

      <h2>8. Intellectual Property</h2>
      <ul>
        <li>
          Unless otherwise stated, the software, site design, original text,
          graphics, branding, logos, game systems, documentation and other original
          materials forming part of the Services are owned by Paul Frederick David
          Walsh or used with permission from their respective rights holders.
        </li>
        <li>
          Third-party names, marks, media and platform assets remain the property
          of their respective owners. Their appearance does not transfer ownership
          to us.
        </li>
        <li>
          You receive a limited, personal, revocable, non-exclusive,
          non-transferable right to use the Services for their intended purpose in
          accordance with these Terms.
        </li>
        <li>
          You must not reproduce, sell, license, publish, adapt, distribute or
          commercially exploit protected Service content without permission unless
          applicable law independently permits the activity.
        </li>
      </ul>

      <h2>9. Service Availability, Changes and Maintenance</h2>
      <p>
        We may modify, update, suspend, discontinue, repair or maintain any part of
        the Services. We do not promise uninterrupted or error-free availability.
        Where reasonably practical, material changes affecting users will be
        communicated through the website, Discord or another appropriate channel.
      </p>

      <h2>10. Suspension and Termination</h2>
      <ul>
        <li>
          We may suspend or terminate access where we reasonably believe you have
          materially breached these Terms, created a security risk, abused the
          Services, harmed other users, interfered with operations or where action
          is required by law or a third-party platform.
        </li>
        <li>
          We may take proportionate interim action while investigating suspected
          misconduct or a security incident.
        </li>
        <li>
          Termination does not affect rights, liabilities or obligations that arose
          before termination or provisions that by their nature should continue,
          including intellectual property, liability, dispute and privacy-related
          provisions.
        </li>
      </ul>

      <h2>11. Disclaimers</h2>
      <p>
        To the fullest extent permitted by law, free and community-based elements
        of the Services are provided on an "as available" basis. We do not warrant
        that every feature will always be available, compatible with every device,
        free from defects or suitable for every user's particular purpose.
      </p>
      <p>
        Information provided through the Services is general information unless a
        particular feature expressly states otherwise. It is not legal, financial,
        medical or other regulated professional advice.
      </p>

      <h2>12. Limitation of Liability</h2>
      <p>
        Nothing in these Terms excludes or limits liability where doing so would be
        unlawful. In particular, nothing excludes or limits liability for death or
        personal injury caused by negligence, fraud or fraudulent
        misrepresentation, or any other liability that cannot lawfully be excluded
        or restricted.
      </p>
      <p>
        Subject to the paragraph above and to any mandatory consumer rights, we are
        not responsible for losses that were not reasonably foreseeable when you
        agreed to these Terms, losses caused by events outside our reasonable
        control, or business losses suffered by a consumer using the Services for
        personal purposes. If you use the Services for business purposes, any
        additional limitation must be interpreted only to the extent permitted by
        applicable law.
      </p>

      <h2>13. Responsibility for Your Misuse</h2>
      <p>
        To the extent permitted by law, you are responsible for losses, claims or
        costs caused by your unlawful use of the Services, your material breach of
        these Terms or your infringement of another person's rights. This provision
        does not require a consumer to compensate us for matters for which the law
        does not permit recovery.
      </p>

      <h2>14. Privacy and Data Protection</h2>
      <p>
        Our collection and use of personal information is described in the Pogforts
        Privacy Policy. That policy explains the categories of information we use,
        purposes and lawful bases, cookies and authentication, third-party sharing,
        retention, individual rights and complaint routes.
      </p>

      <h2>15. Links and External Services</h2>
      <p>
        The Services may contain links to or integrations with websites, APIs,
        payment services, content delivery networks or platforms that we do not
        control. We are not responsible for their independent content, security or
        privacy practices. You should review the terms and privacy information of
        third parties you choose to use.
      </p>

      <h2>16. Changes to These Terms</h2>
      <p>
        We may update these Terms to reflect changes in the Services, law, security
        requirements or operating practices. The current version will be published
        on pogforts.uk. Where a change materially affects existing users, we will
        take reasonable steps to provide notice where required or appropriate.
        Continued use after an updated version takes effect constitutes acceptance
        only to the extent permitted by applicable law.
      </p>

      <h2>17. Governing Law and Disputes</h2>
      <p>
        These Terms are governed by the laws of England and Wales. If you are a
        consumer, you retain any mandatory protections and rights to bring
        proceedings available to you under the law that applies to your place of
        residence. For non-consumer disputes, the courts of England and Wales will
        have jurisdiction unless applicable law requires otherwise.
      </p>

      <h2>18. General Legal Provisions</h2>
      <ul>
        <li>
          If any provision is found invalid or unenforceable, the remaining
          provisions continue in effect to the extent permitted by law.
        </li>
        <li>
          A delay or failure to enforce a provision is not a waiver of the right to
          enforce it later.
        </li>
        <li>
          These Terms, together with policies and transaction-specific terms that
          expressly apply, form the agreement governing your use of the relevant
          Services.
        </li>
      </ul>

      <h2>19. Contact Information</h2>
      <p>
        Questions, reports or concerns about these Terms can be raised through the
        Pogforts Discord server: {" "}
        <a
          href="https://discord.gg/8WF9QQJsVz"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://discord.gg/8WF9QQJsVz
        </a>
      </p>

      <h2>20. Closing Statement</h2>
      <p>
        By continuing to use the Services, you confirm that you have read and agree
        to these Terms of Service, subject to any rights that applicable law gives
        you and does not permit you to waive.
      </p>
    </div>
  );
};

export default TermsOfServices;
